import React, { useEffect, useMemo, useState } from 'react';
import StatusNotification from '../components/StatusNotification';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PlatformAuditLogs from './PlatformAuditLogs';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
const ACCESS_LANGUAGES = [['en', 'English'], ['hi', 'Hindi'], ['hi-en', 'Hinglish'], ['bn', 'Bengali'], ['mr', 'Marathi'], ['gu', 'Gujarati'], ['ta', 'Tamil'], ['te', 'Telugu'], ['kn', 'Kannada'], ['ml', 'Malayalam'], ['pa', 'Punjabi'], ['ur', 'Urdu']];
const formatUsageNumber = (value) => new Intl.NumberFormat().format(Number(value || 0));
const dateInputValue = (date) => new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().slice(0, 10);
const defaultUsageDates = () => {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 29);
  return { from: dateInputValue(from), to: dateInputValue(to) };
};

/** Purpose: administer platform tenants, plans, and platform configuration. Why: these controls operate across workspaces and must remain separate from bot-level settings. */
export default function PlatformControl() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get('section') || 'tenants';
  const serviceOptions = ['voice', 'chat', 'whatsapp'];

  const [message, setMessage] = useState(null);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [usageDates, setUsageDates] = useState(defaultUsageDates);
  const [usageReport, setUsageReport] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [usageError, setUsageError] = useState('');

  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [languageTenant, setLanguageTenant] = useState(null);
  const [allowedLanguages, setAllowedLanguages] = useState(['en']);
  const [languageBusy, setLanguageBusy] = useState(false);
  const [languageError, setLanguageError] = useState('');
  /** Purpose: Handle open languages in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openLanguages = async (tenant) => {
    setLanguageTenant(tenant); setLanguageBusy(true); setLanguageError(''); setAllowedLanguages(['en']);
    try {
      const response = await axios.get(`/api/v1/admin/platform/tenants/${encodeURIComponent(tenant.tenantId)}/languages`);
      setAllowedLanguages(response.data.allowedLanguages);
    } catch (error) { setLanguageError(error.response?.data?.message || error.response?.data?.error || 'Could not load language access'); }
    finally { setLanguageBusy(false); }
  };
  /** Purpose: Handle save languages in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const saveLanguages = async () => {
    setLanguageBusy(true); setLanguageError('');
    try {
      await axios.put(`/api/v1/admin/platform/tenants/${encodeURIComponent(languageTenant.tenantId)}/languages`, { allowedLanguages });
      setLanguageTenant(null);
      setMessage({ type: 'success', text: 'Tenant language access updated.' });
    } catch (error) { setLanguageError(error.response?.data?.message || error.response?.data?.error || 'Could not save language access'); }
    finally { setLanguageBusy(false); }
  };
  const [editingTenantId, setEditingTenantId] = useState(null);
  const [tenantForm, setTenantForm] = useState({
    tenantId: '',
    businessName: '',
    phoneNumber: '',
    plan: '',
    services: ['voice', 'chat', 'whatsapp'],
    isActive: true,
    aiPrompt: '',
  });

  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingPlanCode, setEditingPlanCode] = useState(null);
  const [planForm, setPlanForm] = useState({
    code: '',
    name: '',
    monthlyPrice: 0,
    actualPrice: 0,
    currency: 'INR',
    description: '',
    featuresText: '',
    limits: { messagesPerMonth: 0, voiceMinutesPerMonth: 0, nluInferencesPerMonth: 0, apiExecutionsPerMonth: 0, storageMb: 0 },
    isActive: true,
    sortOrder: 0,
  });

  /** Purpose: Load fetch tenants for the platform control module. Why: The selected view needs current data to render or continue processing. */
  const fetchTenants = async () => {
    try {
      setLoadingTenants(true);
      const response = await axios.get('/api/v1/admin/platform/tenants');
      setTenants(response.data.tenants || []);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to load tenants' });
    } finally {
      setLoadingTenants(false);
    }
  };

  /** Purpose: Load fetch plans for the platform control module. Why: The selected view needs current data to render or continue processing. */
  const fetchPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await axios.get('/api/v1/admin/platform/plans');
      setPlans(response.data.plans || []);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to load plans' });
    } finally {
      setLoadingPlans(false);
    }
  };

  /** Purpose: load provider-reported token totals for the selected period. Why: Super Admins need tenant-level usage visibility without exposing prompts or credentials. */
  const fetchAIUsage = async () => {
    try {
      setLoadingUsage(true);
      setUsageError('');
      const response = await axios.get('/api/v1/admin/ai-usage', { params: usageDates });
      setUsageReport(response.data);
    } catch (error) {
      setUsageError(error.response?.data?.error || 'Could not load AI token usage.');
    } finally {
      setLoadingUsage(false);
    }
  };

  useEffect(() => {
    if (isAdmin()) {
      fetchPlans();
      fetchTenants();
    }
  }, []);

  useEffect(() => {
    if (isAdmin() && section === 'ai-usage') fetchAIUsage();
  }, [section]);

  /** Purpose: Perform filtered tenants for the platform control module. Why: Callers need a consistent result from this shared operation. */
  const filteredTenants = useMemo(() => {
    return tenants.filter((tenant) => {
      const matchPlan = planFilter === 'all' || tenant.plan === planFilter;
      const matchStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' ? tenant.isActive : !tenant.isActive);
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        tenant.businessName?.toLowerCase().includes(q) ||
        tenant.tenantId?.toLowerCase().includes(q) ||
        tenant.phoneNumber?.toLowerCase().includes(q);
      return matchPlan && matchStatus && matchSearch;
    });
  }, [tenants, planFilter, statusFilter, search]);

  /** Purpose: Handle open create tenant in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openCreateTenant = () => {
    setEditingTenantId(null);
    setTenantForm({
      tenantId: '',
      businessName: '',
      phoneNumber: '',
      plan: plans[0]?.code || '',
      services: ['voice', 'chat', 'whatsapp'],
      isActive: true,
      aiPrompt: '',
    });
    setTenantDialogOpen(true);
  };

  /** Purpose: Handle open edit tenant in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openEditTenant = (tenant) => {
    setEditingTenantId(tenant.tenantId);
    setTenantForm({
      tenantId: tenant.tenantId,
      businessName: tenant.businessName || '',
      phoneNumber: tenant.phoneNumber || '',
      plan: tenant.plan || plans[0]?.code || '',
      services: Array.isArray(tenant.services) && tenant.services.length > 0 ? tenant.services : ['voice', 'chat', 'whatsapp'],
      isActive: tenant.isActive !== false,
      aiPrompt: tenant.aiPrompt || '',
    });
    setTenantDialogOpen(true);
  };

  /** Purpose: Handle save tenant in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const saveTenant = async () => {
    try {
      if (!tenantForm.businessName || !tenantForm.phoneNumber || (!editingTenantId && !tenantForm.tenantId)) {
        setMessage({ type: 'error', text: 'Tenant ID, business name, and phone are required' });
        return;
      }

      if (editingTenantId) {
        await axios.put(`/api/v1/admin/platform/tenants/${editingTenantId}`, {
          businessName: tenantForm.businessName,
          phoneNumber: tenantForm.phoneNumber,
          plan: tenantForm.plan,
          services: tenantForm.services,
          isActive: tenantForm.isActive,
          aiPrompt: tenantForm.aiPrompt,
        });
        setMessage({ type: 'success', text: 'Tenant updated successfully' });
      } else {
        await axios.post('/api/v1/admin/platform/tenants', tenantForm);
        setMessage({ type: 'success', text: 'Tenant created successfully' });
      }

      setTenantDialogOpen(false);
      fetchTenants();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save tenant' });
    }
  };

  /** Purpose: Handle open create plan in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openCreatePlan = () => {
    setEditingPlanCode(null);
    setPlanForm({
      code: '',
      name: '',
      monthlyPrice: 0,
      actualPrice: 0,
      currency: 'INR',
      description: '',
      featuresText: '',
      limits: { messagesPerMonth: 0, voiceMinutesPerMonth: 0, nluInferencesPerMonth: 0, apiExecutionsPerMonth: 0, storageMb: 0 },
      isActive: true,
      sortOrder: plans.length,
    });
    setPlanDialogOpen(true);
  };

  /** Purpose: Handle open edit plan in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openEditPlan = (plan) => {
    setEditingPlanCode(plan.code);
    setPlanForm({
      code: plan.code || '',
      name: plan.name || '',
      monthlyPrice: Number(plan.monthlyPrice || 0),
      actualPrice: Number(plan.actualPrice || 0),
      currency: plan.currency || 'INR',
      description: plan.description || '',
      featuresText: Array.isArray(plan.features) ? plan.features.join('\n') : '',
      limits: { messagesPerMonth: 0, voiceMinutesPerMonth: 0, nluInferencesPerMonth: 0, apiExecutionsPerMonth: 0, storageMb: 0, ...(plan.limits || {}) },
      isActive: plan.isActive !== false,
      sortOrder: Number(plan.sortOrder || 0),
    });
    setPlanDialogOpen(true);
  };

  /** Purpose: Handle handle numeric input in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleNumericInput = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return '';
    return String(raw).replace(/^0+(?=\d)/, '');
  };

  /** Purpose: Handle handle numeric focus in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleNumericFocus = (event) => {
    if (String(event.target.value) === '0') {
      event.target.select();
      setTimeout(() => {
        if (event.target && String(event.target.value) === '0') {
          try {
            event.target.select();
          } catch (_) {}
        }
      }, 0);
    }
  };

  /** Purpose: Handle handle limit change in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleLimitChange = (key, event) => {
    const cleaned = handleNumericInput(event.target.value);
    event.target.value = '';
    event.target.value = cleaned;
    setPlanForm((prev) => ({
      ...prev,
      limits: {
        ...(prev.limits || {}),
        [key]: cleaned,
      },
    }));
  };

  /** Purpose: Handle handle limit blur in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleLimitBlur = (key, event) => {
    if (event.target.value === '') {
      setPlanForm((prev) => ({
        ...prev,
        limits: {
          ...(prev.limits || {}),
          [key]: 0,
        },
      }));
    }
  };

  /** Purpose: Handle handle monthly price change in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleMonthlyPriceChange = (event) => {
    const cleaned = handleNumericInput(event.target.value);
    event.target.value = '';
    event.target.value = cleaned;
    setPlanForm((prev) => ({
      ...prev,
      monthlyPrice: cleaned,
    }));
  };

  /** Purpose: Handle handle monthly price blur in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleMonthlyPriceBlur = (event) => {
    if (event.target.value === '') {
      setPlanForm((prev) => ({ ...prev, monthlyPrice: 0 }));
    }
  };

  /** Purpose: Handle handle actual price change in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleActualPriceChange = (event) => {
    const cleaned = handleNumericInput(event.target.value);
    event.target.value = '';
    event.target.value = cleaned;
    setPlanForm((prev) => ({
      ...prev,
      actualPrice: cleaned,
    }));
  };

  /** Purpose: Handle handle actual price blur in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleActualPriceBlur = (event) => {
    if (event.target.value === '') {
      setPlanForm((prev) => ({ ...prev, actualPrice: 0 }));
    }
  };

  /** Purpose: Handle save plan in the platform control module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const savePlan = async () => {
    try {
      if (!planForm.code || !planForm.name) {
        setMessage({ type: 'error', text: 'Plan code and name are required' });
        return;
      }

      const payload = {
        code: planForm.code,
        name: planForm.name,
        monthlyPrice: Math.max(0, Number(planForm.monthlyPrice) || 0),
        actualPrice: Math.max(0, Number(planForm.actualPrice) || 0),
        currency: planForm.currency || 'INR',
        description: planForm.description || '',
        features: (planForm.featuresText || '')
          .split('\n')
          .map((f) => f.trim())
          .filter(Boolean),
        limits: {
          messagesPerMonth: Math.max(0, Number(planForm.limits?.messagesPerMonth) || 0),
          voiceMinutesPerMonth: Math.max(0, Number(planForm.limits?.voiceMinutesPerMonth) || 0),
          nluInferencesPerMonth: Math.max(0, Number(planForm.limits?.nluInferencesPerMonth) || 0),
          apiExecutionsPerMonth: Math.max(0, Number(planForm.limits?.apiExecutionsPerMonth) || 0),
          storageMb: Math.max(0, Number(planForm.limits?.storageMb) || 0),
        },
        isActive: planForm.isActive,
        sortOrder: Number(planForm.sortOrder) || 0,
      };

      if (editingPlanCode) {
        await axios.put(`/api/v1/admin/platform/plans/${editingPlanCode}`, payload);
        setMessage({ type: 'success', text: 'Plan updated successfully' });
      } else {
        await axios.post('/api/v1/admin/platform/plans', payload);
        setMessage({ type: 'success', text: 'Plan created successfully' });
      }

      setPlanDialogOpen(false);
      fetchPlans();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save plan' });
    }
  };

  if (!isAdmin()) {
    return <Alert severity="error">Access denied. Super admin only.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
          Platform Control
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {section === 'audit' || section === 'audit-logs'
            ? 'Immutable audit trail of security-critical actions, tenant deletions, logins, and API updates.'
            : section === 'ai-usage'
              ? 'Provider-reported Dynamic Chat token consumption by tenant.'
              : 'Manage tenants and pricing plans from one place.'}
        </Typography>
      </Box>

      <StatusNotification message={message} onClose={() => setMessage(null)} />

      <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
        <Button
          variant={section === 'tenants' ? 'contained' : 'outlined'}
          onClick={() => setSearchParams({ section: 'tenants' })}
        >
          Tenants
        </Button>
        <Button
          variant={section === 'plans-pricing' ? 'contained' : 'outlined'}
          onClick={() => setSearchParams({ section: 'plans-pricing' })}
        >
          Plans & Pricing
        </Button>
        <Button
          variant={section === 'audit' || section === 'audit-logs' ? 'contained' : 'outlined'}
          onClick={() => setSearchParams({ section: 'audit' })}
        >
          Platform Audit Logs
        </Button>
        <Button
          variant={section === 'ai-usage' ? 'contained' : 'outlined'}
          onClick={() => setSearchParams({ section: 'ai-usage' })}
        >
          AI Token Usage
        </Button>
        <Button variant="outlined" onClick={() => navigate('/bots')}>
          All Bots
        </Button>
      </Box>

      {section === 'tenants' && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                label="Search tenant"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Plan</InputLabel>
                <Select value={planFilter} label="Plan" onChange={(e) => setPlanFilter(e.target.value)}>
                  <MenuItem value="all">All Plans</MenuItem>
                  {plans.map((plan) => (
                    <MenuItem key={plan.code} value={plan.code}>{plan.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateTenant}>
                Add Tenant
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant Name</TableCell>
                  <TableCell>Tenant ID</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Bots</TableCell>
                  <TableCell>Services</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTenants.map((tenant) => (
                  <TableRow key={tenant.tenantId}>
                    <TableCell>{tenant.businessName}</TableCell>
                    <TableCell>{tenant.tenantId}</TableCell>
                    <TableCell>{tenant.phoneNumber}</TableCell>
                    <TableCell>
                      <Tooltip title="Click to change plan">
                        <Chip
                          label={tenant.plan}
                          size="small"
                          variant="outlined"
                          clickable
                          onClick={() => openEditTenant(tenant)}
                          sx={{ cursor: 'pointer', fontWeight: 600 }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tenant.isActive ? 'active' : 'inactive'}
                        size="small"
                        color={tenant.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{tenant.botCount || 0}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(tenant.services || []).map((service) => (
                          <Chip key={service} size="small" label={service} variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>{tenant.updatedAt ? new Date(tenant.updatedAt).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit tenant details & plan">
                        <IconButton size="small" onClick={() => openEditTenant(tenant)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Button size="small" onClick={() => openLanguages(tenant)}>Languages</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!loadingTenants && filteredTenants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">No tenants found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <Dialog open={Boolean(languageTenant)} onClose={() => !languageBusy && setLanguageTenant(null)} fullWidth maxWidth="sm">
        <DialogTitle>Language access · {languageTenant?.businessName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>English is always included. Select additional languages this tenant’s bot admins may enable. Existing translations are preserved.</Typography>
          {languageError && <Alert severity="error" sx={{ mb: 2 }}>{languageError}</Alert>}
          <Box display="flex" gap={1} flexWrap="wrap" onClick={() => setLanguageError('')}>
            {ACCESS_LANGUAGES.map(([code, label]) => <Chip key={code} label={`${label} (${code})`} color={allowedLanguages.includes(code) ? 'primary' : 'default'} variant={allowedLanguages.includes(code) ? 'filled' : 'outlined'} disabled={code === 'en' || languageBusy} onClick={() => setAllowedLanguages((current) => current.includes(code) ? current.filter((item) => item !== code) : [...current, code])} />)}
          </Box>
        </DialogContent>
        <DialogActions><Button disabled={languageBusy} onClick={() => setLanguageTenant(null)}>Cancel</Button><Button variant="contained" disabled={languageBusy || Boolean(languageError)} onClick={saveLanguages}>Save access</Button></DialogActions>
      </Dialog>

      {section === 'plans-pricing' && (
        <>
          <Paper sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Plans & Pricing</Typography>
              <Typography variant="body2" color="text.secondary">
                These plans are used in signup and tenant creation.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreatePlan}>
              Create Plan
            </Button>
          </Paper>

          <Paper sx={{ overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Actual Price (MRP)</TableCell>
                  <TableCell>Discounted Price (Offer)</TableCell>
                  <TableCell>Discount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map((plan) => {
                  const actual = Number(plan.actualPrice || 0);
                  const discounted = Number(plan.monthlyPrice || 0);
                  const hasDiscount = actual > discounted;
                  const discountPercent = hasDiscount ? Math.round(((actual - discounted) / actual) * 100) : 0;

                  return (
                    <TableRow key={plan.code}>
                      <TableCell sx={{ fontWeight: 600 }}>{plan.code}</TableCell>
                      <TableCell>{plan.name}</TableCell>
                      <TableCell>
                        {hasDiscount ? (
                          <Typography
                            variant="body2"
                            sx={{ textDecoration: 'line-through', color: 'text.secondary', fontWeight: 500 }}
                          >
                            {plan.currency} {actual.toLocaleString()}
                          </Typography>
                        ) : actual > 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            {plan.currency} {actual.toLocaleString()}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {plan.currency} {discounted.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {hasDiscount ? (
                          <Chip
                            label={`${discountPercent}% OFF`}
                            size="small"
                            color="success"
                            sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">Regular</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={plan.isActive ? 'active' : 'inactive'}
                          size="small"
                          color={plan.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => openEditPlan(plan)}>
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!loadingPlans && plans.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No plans found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {(section === 'audit' || section === 'audit-logs') && (
        <PlatformAuditLogs embedded={true} />
      )}

      {section === 'ai-usage' && (
        <>
          <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              type="date"
              label="From date"
              InputLabelProps={{ shrink: true }}
              value={usageDates.from}
              onChange={(event) => setUsageDates((current) => ({ ...current, from: event.target.value }))}
            />
            <TextField
              size="small"
              type="date"
              label="To date"
              InputLabelProps={{ shrink: true }}
              value={usageDates.to}
              onChange={(event) => setUsageDates((current) => ({ ...current, to: event.target.value }))}
            />
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              disabled={loadingUsage || !usageDates.from || !usageDates.to}
              onClick={fetchAIUsage}
            >
              {loadingUsage ? 'Loading…' : 'Refresh usage'}
            </Button>
          </Paper>

          {usageError && <Alert severity="error" sx={{ mb: 2 }}>{usageError}</Alert>}

          {!usageReport && loadingUsage && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              Loading AI token usage…
            </Paper>
          )}

          {usageReport && (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 1.5, mb: 2 }}>
                {[
                  ['Total tokens', usageReport.totals.totalTokens],
                  ['Input tokens', usageReport.totals.inputTokens],
                  ['Output tokens', usageReport.totals.outputTokens],
                  ['AI requests', usageReport.totals.requests],
                ].map(([label, value]) => (
                  <Paper key={label} variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 750, letterSpacing: '-0.03em' }}>
                      {formatUsageNumber(value)}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                Counts come from provider usage metadata for Dynamic Chat only. {formatUsageNumber(usageReport.totals.reportedRequests)} of {formatUsageNumber(usageReport.totals.requests)} requests reported token usage; {formatUsageNumber(usageReport.totals.incompleteRequests)} have missing or partial counts. Older requests are not backfilled, and prompts/API keys are never shown here.
              </Alert>

              <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.75, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Usage by tenant</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {usageReport.range.from} – {usageReport.range.to}
                  </Typography>
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tenant</TableCell>
                        <TableCell>Tenant ID</TableCell>
                        <TableCell align="right">AI requests</TableCell>
                        <TableCell align="right">Input tokens</TableCell>
                        <TableCell align="right">Output tokens</TableCell>
                        <TableCell align="right">Total tokens</TableCell>
                        <TableCell align="right">Usage coverage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usageReport.tenants.map((tenant) => (
                        <TableRow key={tenant.tenantId} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{tenant.tenantName}</TableCell>
                          <TableCell>{tenant.tenantId}</TableCell>
                          <TableCell align="right">{formatUsageNumber(tenant.requests)}</TableCell>
                          <TableCell align="right">{formatUsageNumber(tenant.inputTokens)}</TableCell>
                          <TableCell align="right">{formatUsageNumber(tenant.outputTokens)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{formatUsageNumber(tenant.totalTokens)}</TableCell>
                          <TableCell align="right">
                            {formatUsageNumber(tenant.reportedRequests)} / {formatUsageNumber(tenant.requests)}
                            {tenant.incompleteRequests > 0 && <Typography component="span" variant="caption" color="warning.main"> · partial</Typography>}
                          </TableCell>
                        </TableRow>
                      ))}
                      {usageReport.tenants.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                            {loadingUsage ? 'Loading usage…' : 'No Dynamic Chat usage recorded for this period.'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            </>
          )}
        </>
      )}

      <Dialog open={tenantDialogOpen} onClose={() => setTenantDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTenantId ? 'Edit Tenant' : 'Create Tenant'}</DialogTitle>
        <DialogContent>
          <TextField margin="dense" fullWidth label="Tenant ID" value={tenantForm.tenantId} disabled={Boolean(editingTenantId)} onChange={(e) => setTenantForm((prev) => ({ ...prev, tenantId: e.target.value }))} />
          <TextField margin="dense" fullWidth label="Tenant Name" value={tenantForm.businessName} onChange={(e) => setTenantForm((prev) => ({ ...prev, businessName: e.target.value }))} />
          <TextField margin="dense" fullWidth label="Phone Number" value={tenantForm.phoneNumber} onChange={(e) => setTenantForm((prev) => ({ ...prev, phoneNumber: e.target.value }))} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Plan</InputLabel>
            <Select value={tenantForm.plan} label="Plan" onChange={(e) => setTenantForm((prev) => ({ ...prev, plan: e.target.value }))}>
              {plans.map((plan) => (
                <MenuItem key={plan.code} value={plan.code}>{plan.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Services</InputLabel>
            <Select
              multiple
              value={tenantForm.services}
              label="Services"
              onChange={(e) => {
                const value = e.target.value;
                const next = typeof value === 'string' ? value.split(',') : value;
                setTenantForm((prev) => ({
                  ...prev,
                  services: next.includes('all') ? serviceOptions : next,
                }));
              }}
            >
              <MenuItem value="all"><em>All</em></MenuItem>
              {serviceOptions.map((service) => (
                <MenuItem key={service} value={service}>{service}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select value={tenantForm.isActive ? 'active' : 'inactive'} label="Status" onChange={(e) => setTenantForm((prev) => ({ ...prev, isActive: e.target.value === 'active' }))}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTenantDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveTenant}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={planDialogOpen} onClose={() => setPlanDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPlanCode ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
        <DialogContent>
          <TextField margin="dense" fullWidth label="Code" value={planForm.code} disabled={Boolean(editingPlanCode)} onChange={(e) => setPlanForm((prev) => ({ ...prev, code: e.target.value.toLowerCase() }))} />
          <TextField margin="dense" fullWidth label="Name" value={planForm.name} onChange={(e) => setPlanForm((prev) => ({ ...prev, name: e.target.value }))} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, my: 0.5 }}>
            <TextField
              margin="dense"
              fullWidth
              type="number"
              label="Actual Price (MRP)"
              placeholder="e.g. 19999"
              value={planForm.actualPrice ?? ''}
              inputProps={{ min: 0 }}
              onFocus={handleNumericFocus}
              onChange={handleActualPriceChange}
              onBlur={handleActualPriceBlur}
              helperText="Original / Strikethrough price"
            />
            <TextField
              margin="dense"
              fullWidth
              type="number"
              label="Discounted Price (Offer)"
              placeholder="e.g. 9999"
              value={planForm.monthlyPrice ?? ''}
              inputProps={{ min: 0 }}
              onFocus={handleNumericFocus}
              onChange={handleMonthlyPriceChange}
              onBlur={handleMonthlyPriceBlur}
              helperText={
                Number(planForm.actualPrice || 0) > Number(planForm.monthlyPrice || 0)
                  ? `Save ${planForm.currency || 'INR'} ${(Number(planForm.actualPrice) - Number(planForm.monthlyPrice)).toLocaleString()} (${Math.round(((Number(planForm.actualPrice) - Number(planForm.monthlyPrice)) / Number(planForm.actualPrice)) * 100)}% OFF)`
                  : 'Effective monthly selling price'
              }
            />
          </Box>
          <TextField margin="dense" fullWidth label="Currency" value={planForm.currency} onChange={(e) => setPlanForm((prev) => ({ ...prev, currency: e.target.value }))} />
          <TextField margin="dense" fullWidth multiline minRows={3} label="Description" value={planForm.description} onChange={(e) => setPlanForm((prev) => ({ ...prev, description: e.target.value }))} />
          <TextField margin="dense" fullWidth multiline minRows={3} label="Features (one per line)" value={planForm.featuresText} onChange={(e) => setPlanForm((prev) => ({ ...prev, featuresText: e.target.value }))} />
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Monthly usage limits (0 = unlimited)</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            {[
              ['messagesPerMonth', 'Messages'],
              ['voiceMinutesPerMonth', 'Voice minutes'],
              ['nluInferencesPerMonth', 'NLU inferences'],
              ['apiExecutionsPerMonth', 'API executions'],
              ['storageMb', 'Storage MB'],
            ].map(([key, label]) => (
              <TextField
                key={key}
                margin="dense"
                type="number"
                label={label}
                value={planForm.limits?.[key] ?? ''}
                inputProps={{ min: 0 }}
                onFocus={handleNumericFocus}
                onChange={(e) => handleLimitChange(key, e)}
                onBlur={(e) => handleLimitBlur(key, e)}
              />
            ))}
          </Box>
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select value={planForm.isActive ? 'active' : 'inactive'} label="Status" onChange={(e) => setPlanForm((prev) => ({ ...prev, isActive: e.target.value === 'active' }))}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePlan}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
